import { AccountsService } from 'base/accounts_service';
import { AccountStore } from 'base/account_store';
import { AnalyticsService } from 'base/analytics';
import { Button } from 'base/ui/button';
import { Dialog } from 'base/ui/dialog';
import { IconClear, IconGift, IconLink } from 'base/ui/icons';
import { Inline, Rows } from 'base/ui/layout';
import { Link } from 'base/ui/link';
import { LoadingDots } from 'base/ui/loading_dots';
import { mount, overlayStore } from 'base/ui/overlay';
import { TickRow } from 'base/ui/tick_row';
import { ToastQueue } from 'base/ui/toast';
import {
  TextMedium,
  TextRegularLight,
  TextSmall,
  TextTinyLight,
  Title3,
} from 'base/ui/typography';
import communityImage from 'blocks/dashboard/community.file.png';
import iconGiftUrl from 'blocks/dashboard/icon-gift.file.svg';
import { useReferralLink } from 'blocks/use_referral_link';
import React from 'react';
import styles from './referral_earning.css';

const KEY = 'SEEN_REFERRAL_DIALOG';
function hasSeenReferralEarningDialog() {
  return localStorage.getItem(KEY) === 'true';
}
function markReferralEarningDialogSeen() {
  localStorage.setItem(KEY, 'true');
}
function isAccountQualified(accountStore: AccountStore) {
  const dateJoined = accountStore.member?.dateJoined;
  if (!dateJoined) return false;

  const joinedDate = new Date(dateJoined);
  const currentDate = new Date();

  const diffInDays =
    (currentDate.getTime() - joinedDate.getTime()) / (1000 * 60 * 60 * 24);

  return diffInDays > 2;
}
export function installReferralEarningDialog({
  accountsService,
  analyticsService,
  accountStore,
  toastQueue,
}: {
  accountsService: AccountsService;
  analyticsService: AnalyticsService;
  accountStore: AccountStore;
  toastQueue: ToastQueue;
}) {
  if (hasSeenReferralEarningDialog()) return;
  if (!isAccountQualified(accountStore)) {
    return;
  }

  if (!overlayStore.isDialogOpen) {
    markReferralEarningDialogSeen();
    const closeDialog = mount(
      <ReferralEarningDialog
        onClose={() => closeDialog()}
        toastQueue={toastQueue}
        analyticsService={analyticsService}
        accountsService={accountsService}
        optInShown
      />,
    );
  }
}

export const ReferralEarningDialog = ({
  toastQueue,
  onClose,
  accountsService,
  analyticsService,
  optInShown,
}: {
  toastQueue: ToastQueue;
  onClose: () => void;
  accountsService: AccountsService;
  analyticsService: AnalyticsService;
  optInShown?: boolean;
}) => {
  const [isCopyLinkShowed, setIsCopyLinkedShowed] = React.useState(!optInShown);
  const linkDataState = useReferralLink({
    accountsService,
    enabled: isCopyLinkShowed,
  });
  const hasTrackedDialogViewed = React.useRef(false);
  const { url } = linkDataState;
  React.useEffect(() => {
    if (optInShown && !hasTrackedDialogViewed.current) {
      analyticsService.track({
        event: 'referral_flow:dialog_viewed',
      });
      hasTrackedDialogViewed.current = true;
    }
  }, [optInShown, analyticsService]);

  const hasTrackReferralLinkViewed = React.useRef(false);
  React.useEffect(() => {
    if (url && !hasTrackReferralLinkViewed.current) {
      analyticsService.track({
        event: 'referral_flow:link_viewed',
        link: url,
      });
      hasTrackReferralLinkViewed.current = true;
    }
  }, [url, analyticsService]);

  return (
    <Dialog
      onClose={onClose}
      title={
        isCopyLinkShowed
          ? 'Refer a friend and earn $10 credit for every paying sign up!'
          : ''
      }
      width="regular"
    >
      {!isCopyLinkShowed ? (
        <div>
          <div className={styles.content}>
            <Rows xalign="center" spacing={16}>
              <img src={iconGiftUrl} />
              <Title3 align="center">Invite friends and earn rewards!</Title3>
              <TextRegularLight align="center">
                Help others discover Relume and get rewarded with{' '}
                <b>$10 credit.</b>
              </TextRegularLight>
            </Rows>
          </div>
          <Rows xalign="center" spacing={16}>
            <Button
              variant="primary"
              fullWidth
              label="Invite friends"
              onClick={() => {
                analyticsService.track({
                  event: 'referral_flow:invite_friends_click',
                });
                setIsCopyLinkedShowed(true);
              }}
            />
            <Button
              fullWidth
              variant="secondary"
              label="Later"
              onClick={onClose}
            />
          </Rows>
        </div>
      ) : (
        <>
          <img className={styles.heroImage} src={communityImage} alt="" />
          <TextMedium>How it works</TextMedium>
          <Rows spacing={40}>
            <Rows spacing={8}>
              <TickRow type="one" size="medium">
                Share your referral link.
              </TickRow>
              <TickRow type="two">
                Your friend gets 50% off their first monthly plan
              </TickRow>
              <TickRow type="three">
                You earn $10 credit for every paying signup
              </TickRow>
            </Rows>
            <Rows spacing={24}>
              <Inline spacing={8} xalign="center">
                {!url ? (
                  <div className={styles.loadingContainer}>
                    <LoadingDots />
                  </div>
                ) : (
                  <>
                    <div className={styles.linkContainer}>
                      <TextTinyLight selectable>{url}</TextTinyLight>
                    </div>
                    <div className={styles.buttonContainer}>
                      <Button
                        variant="primary"
                        size="large"
                        icon={IconLink}
                        label="Copy Link"
                        fullWidth
                        onClick={() => {
                          analyticsService.track({
                            event: 'referral_flow:link_copied',
                            link: url,
                          });
                          navigator.clipboard.writeText(url);
                          toastQueue.push({ label: 'Copied to clipboard' });
                        }}
                      />
                    </div>
                  </>
                )}
              </Inline>
              <TextTinyLight>
                Sharing your link means you agree to our terms and conditions.
                <br />
                <Link target="_self" href={'/app/manage-account/referral'}>
                  Learn more
                </Link>
              </TextTinyLight>
            </Rows>
          </Rows>
        </>
      )}
    </Dialog>
  );
};

const ReferralEarningBanner = ({
  onClose,
  onInviteClicked,
}: {
  onClose: () => void;
  onInviteClicked: () => void;
}) => {
  return (
    <div className={styles.bannerContainer}>
      <div className={styles.bannerContent}>
        <Inline spacing={4} yalign="center">
          <IconGift width={14} height={14} color="white" />
          <TextSmall color="white">
            Earn $10 credit per referral—give friends 50% off their first plan!
          </TextSmall>
          <Button variant="subtle" onClick={onInviteClicked}>
            <div className={styles.inviteText}>
              <TextSmall>Invite & earn</TextSmall>
            </div>
          </Button>
        </Inline>
      </div>
      <Button
        icon={IconClear}
        variant="subtle"
        size="small"
        onClick={onClose}
      />
    </div>
  );
};

const EARNING_BANNER_KEY = 'SEEN_REFERRAL_BANNER';
function hasDismissedReferralEarningBanner() {
  return localStorage.getItem(EARNING_BANNER_KEY) === 'true';
}
function markReferralEarningBannerDismissed() {
  localStorage.setItem(EARNING_BANNER_KEY, 'true');
}
export function installReferralEarningBanner({
  accountStore,
  toastQueue,
  analyticsService,
}: {
  accountStore: AccountStore;
  toastQueue: ToastQueue;
  analyticsService: AnalyticsService;
}) {
  if (hasDismissedReferralEarningBanner()) return;
  if (!isAccountQualified(accountStore)) {
    return;
  }

  if (!overlayStore.currentFloatingBanner) {
    overlayStore.openFloatingBanner(
      <ReferralEarningBanner
        onClose={() => {
          markReferralEarningBannerDismissed();
          overlayStore.closeFloatingBanner();
        }}
        onInviteClicked={() => {
          analyticsService.track({
            event: 'referral_flow:invite_banner_clicked',
          });
          const close = mount(
            <ReferralEarningDialog
              analyticsService={analyticsService}
              toastQueue={toastQueue}
              onClose={() => close()}
              accountsService={accountStore.service}
            />,
          );
        }}
      />,
    );
  }
}
